import { StringUtil } from "./string.util";

export class DocumentUtil {
    public static readonly cnpjMaskPatterns = {
        A: { pattern: /[A-Z0-9]/i },
        "0": { pattern: /[0-9]/ },
    };

    private static _isLastChar(index: number, input: string): boolean {
        return index === input.length - 1;
    }

    /**
     * Remove caracteres de formatação de um CPF/CNPJ.
     *
     * Caso o valor contenha letras, mantém apenas caracteres alfanuméricos.
     * Caso contrário, mantém apenas números.
     *
     * @param value O valor que terá a formatação removida.
     * @returns O valor sem caracteres de formatação.
     *
     * @example
     * DocumentUtil.clearFormat('00.123.123/0001-12'); // Retorna: '00123123000112'
     * DocumentUtil.clearFormat('XH.BYE.J7E/0001-67'); // Retorna: 'XHBYEJ7E000167'
     */
    public static clearFormat(value: string): string {
        if (StringUtil.isEmpty(value)) {
            return "";
        }
        return /[a-zA-Z]/.test(value)
            ? value.replace(/[^a-zA-Z0-9]/g, "")
            : value.replace(/\D/g, "");
    }

    public static isValidCpf(cpf: string): boolean {
        if (StringUtil.isEmpty(cpf)) {
            return false;
        }

        cpf = StringUtil.removeAccents(cpf);

        if (
            cpf.length !== 11 ||
            cpf === "00000000000" ||
            cpf === "11111111111" ||
            cpf === "22222222222" ||
            cpf === "33333333333" ||
            cpf === "44444444444" ||
            cpf === "55555555555" ||
            cpf === "66666666666" ||
            cpf === "77777777777" ||
            cpf === "88888888888" ||
            cpf === "99999999999"
        ) {
            return false;
        }

        let add = 0;
        for (let i = 0; i < 9; i++) {
            add += +cpf.charAt(i) * (10 - i);
        }
        let rev = 11 - (add % 11);

        if (rev === 10 || rev === 11) {
            rev = 0;
        }

        if (rev !== +cpf.charAt(9)) {
            return false;
        }

        add = 0;
        for (let i = 0; i < 10; i++) {
            add += +cpf.charAt(i) * (11 - i);
        }
        rev = 11 - (add % 11);

        if (rev === 10 || rev === 11) {
            rev = 0;
        }

        if (rev !== +cpf.charAt(10)) {
            return false;
        }

        return true;
    }

    /**
     * Valida um CNPJ numérico ou alfanumérico.
     *
     * A função aceita CNPJ com ou sem máscara, remove os caracteres de formatação
     * e valida o dígito verificador conforme a regra oficial baseada no valor ASCII
     * do caractere subtraído de 48 (`charCodeAt(0) - 48`).
     *
     * Regras aplicadas:
     * - aceita CNPJ numérico e alfanumérico;
     * - aceita valores com máscara, como `12.345.678/0001-90`;
     * - aceita letras minúsculas, convertendo internamente para maiúsculas;
     * - exige exatamente 14 caracteres após a limpeza;
     * - exige que os 2 últimos caracteres sejam dígitos verificadores numéricos;
     * - rejeita sequências inválidas repetidas, como `00000000000000`, `11111111111111`, `AAAAAAAAAAAA00`, entre outras;
     * - rejeita caracteres não permitidos.
     *
     * @param cnpj O CNPJ que será validado.
     * @returns `true` se o CNPJ for válido, caso contrário `false`.
     *
     * @example
     * DocumentUtil.isValidCnpj('12.345.678/0001-90'); // Retorna: true ou false conforme os dígitos verificadores.
     * DocumentUtil.isValidCnpj('12ABC3450001XX'); // Retorna: false, pois os dois últimos caracteres devem ser números.
     * DocumentUtil.isValidCnpj('AAAAAAAAAAAA00'); // Retorna: false.
     */
    public static isValidCnpj(cnpj: string): boolean {
        if (StringUtil.isEmpty(cnpj)) {
            return false;
        }

        if (/[^A-Z0-9./-]/i.test(cnpj)) {
            return false;
        }

        const cleanCnpj = this.clearFormat(cnpj).toUpperCase();

        if (!/^[A-Z0-9]{12}\d{2}$/.test(cleanCnpj)) {
            return false;
        }

        if (/^([A-Z0-9])\1{13}$/.test(cleanCnpj)) {
            return false;
        }

        if (/^([A-Z0-9])\1{11}\d{2}$/.test(cleanCnpj)) {
            return false;
        }

        let size = 12;
        let numbers = cleanCnpj.substring(0, size);
        const digits = cleanCnpj.substring(size);

        let sum = 0;
        let weight = 2;

        for (let i = size - 1; i >= 0; i--) {
            const charValue = numbers.charCodeAt(i) - 48;

            sum += charValue * weight;
            weight = weight === 9 ? 2 : weight + 1;
        }

        let remainder = sum % 11;
        let result = remainder < 2 ? 0 : 11 - remainder;

        if (result !== parseInt(digits.charAt(0), 10)) {
            return false;
        }

        size = 13;
        numbers = cleanCnpj.substring(0, size);

        sum = 0;
        weight = 2;

        for (let i = size - 1; i >= 0; i--) {
            const charValue = numbers.charCodeAt(i) - 48;

            sum += charValue * weight;
            weight = weight === 9 ? 2 : weight + 1;
        }

        remainder = sum % 11;
        result = remainder < 2 ? 0 : 11 - remainder;

        return result === parseInt(digits.charAt(1), 10);
    }

    /**
     * Formata um CPF.
     * @param cpf O CPF a ser formatado.
     * @param pad Se `true`, o CPF será preenchido com zeros à esquerda.
     * @returns O CPF formatado.
     * @example
     * DocumentUtil.formatCpf('12345678909'); // Retorna `123.456.789-09`.
     * DocumentUtil.formatCpf('123456789', true); // Retorna `001.234.567-89`.
     */
    public static formatCpf(cpf: string, pad?: boolean): string {
        if (StringUtil.isEmpty(cpf)) {
            return "";
        }

        // Mantém apenas números e asteriscos
        let digits = cpf.replace(/[^\d*]/g, "");
        if (pad) {
            digits = digits.padStart(11, "0");
        }
        const DOT_INDEXES = [2, 5];
        const HYPHEN_INDEXES = [8];

        return digits
            .slice(0, 11)
            .split("")
            .reduce((acc, digit, i) => {
                const result = `${acc}${digit}`;
                if (!this._isLastChar(i, digits)) {
                    if (DOT_INDEXES.indexOf(i) >= 0) return `${result}.`;
                    if (HYPHEN_INDEXES.indexOf(i) >= 0) return `${result}-`;
                }
                return result;
            }, "");
    }

    /**
     * Formata um CNPJ numérico ou alfanumérico.
     *
     * A função remove automaticamente caracteres de formatação antes de aplicar
     * a máscara padrão de CNPJ: `00.000.000/0000-00`.
     *
     * Também suporta:
     * - CNPJ alfanumérico;
     * - preenchimento automático com zeros à esquerda (`pad`);
     * - formatação parcial para utilização em campos de digitação (`allowPartial`).
     *
     * Quando `allowPartial` estiver ativado, a máscara será aplicada
     * gradualmente conforme a quantidade de caracteres informados.
     *
     * Caso `allowPartial` esteja desativado, apenas valores com 14 caracteres
     * serão formatados. Caso contrário, será retornada uma string vazia.
     *
     * @param cnpj O CNPJ que será formatado.
     * @param options Opções de formatação.
     * @param options.pad Define se o valor deve ser preenchido com zeros à esquerda até atingir 14 caracteres.
     * @param options.allowPartial Define se a máscara parcial deve ser aplicada durante a digitação.
     *
     * @returns O CNPJ formatado ou uma string vazia caso o valor seja inválido.
     *
     * @example
     * DocumentUtil.formatCnpj('12345678000190'); // Retorna: '12.345.678/0001-90'
     * DocumentUtil.formatCnpj('12ABC3450001XX'); // Retorna: '12.ABC.345/0001-XX'
     * DocumentUtil.formatCnpj('1234567890', { pad: true }); // Retorna: '00.001.234/5678-90'
     * DocumentUtil.formatCnpj('12ABC345', { allowPartial: true }); // Retorna: '12.ABC.345'
     * DocumentUtil.formatCnpj('123'); // Retorna: ''
     */
    public static formatCnpj(
        cnpj: string,
        options?: { pad?: boolean; allowPartial?: boolean },
    ): string {
        const pad = options?.pad ?? false;
        const allowPartial = options?.allowPartial ?? false;

        let cleanCnpj = this.clearFormat(cnpj).toUpperCase();
        if (pad) {
            cleanCnpj = cleanCnpj.padStart(14, "0");
        }

        cleanCnpj = cleanCnpj.slice(0, 14);

        const size = cleanCnpj.length;

        if (allowPartial) {
            if (size <= 2) {
                return cleanCnpj;
            }
            if (size <= 5) {
                return `${cleanCnpj.slice(0, 2)}.${cleanCnpj.slice(2)}`;
            }
            if (size <= 8) {
                return `${cleanCnpj.slice(0, 2)}.${cleanCnpj.slice(2, 5)}.${cleanCnpj.slice(5)}`;
            }
            if (size <= 12) {
                return `${cleanCnpj.slice(0, 2)}.${cleanCnpj.slice(2, 5)}.${cleanCnpj.slice(5, 8)}/${cleanCnpj.slice(8)}`;
            }
        } else if (size !== 14) {
            return "";
        }
        return cleanCnpj.replace(
            /(.{2})(.{3})(.{3})(.{4})(.{1,2})/,
            "$1.$2.$3/$4-$5",
        );
    }
}
